/**
 * The case's head (docs/19 A3, part 1): the file number, **the identifier
 * line**, the stage, and **every deadline running, each saying whether it is
 * statutory or set by the office** — an officer who takes a self-imposed
 * deadline for law refuses an extension he is entitled to grant.
 *
 * **The identifier line** (Phase A, docs/24 item 5) — `43/2025 · Deepak Verma ·
 * 53318 · Assembly section · Abusive language to supervisor (14.05) · …` — is
 * English and built on the server from his record and the case record, never
 * composed here. A case not yet numbered (an intake nobody has confirmed) says
 * *Not yet numbered* where the number would be, and offers the way back to its
 * intake.
 *
 * The periods are read through `readPeriod()`, the one wording function, so the
 * head, the card and the register cannot disagree about a date. The stage and
 * every label come off the case's pinned definition (stop list 6).
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { CaseDetail, ClockView, IntakeEnglishView } from '../../api/client'
import { ClockPill, CitationChip } from '../../components/bits'
import { readDate, readPeriod } from '../../lib/period'
import { runningClocks } from './acting'

export function CaseHeader({
  data,
  readings,
  cgm,
  onAsk,
}: {
  data: CaseDetail
  readings: IntakeEnglishView | null
  cgm: boolean
  onAsk: () => void
}) {
  const employee = data.employee as Record<string, string | null>
  const nameEn = (employee['name_en'] ?? '').trim()
  const nameHi = employee['name_hi'] ?? ''
  const stage = (data.stages ?? []).find((s) => s.id === data.stage) ?? null
  // The one place on the page a running deadline is stated: the card under
  // this head leaves its own period out when it is listed here (`acting`).
  const running = runningClocks(data.clocks)
  const identity = useQuery({
    queryKey: ['case-identifier', data.id],
    queryFn: () => api.getCaseIdentifier(data.id),
  })
  const line = identity.data?.after_number ?? null
  const numbered = Boolean(data.file_number)

  return (
    <header className="casehead" data-testid="case-head">
      <div className="ident">
        {/* The file number is the case's identity, so it is the heading — and
            an intake nobody has confirmed has none yet, and says so. */}
        <h1 className={numbered ? 'mono deva' : undefined} data-testid="case-file-number">
          {data.file_number ?? identity.data?.file_number_label ?? 'Not yet numbered'}
        </h1>
        <span className="chip neutral mono">{data.state}</span>
        {!numbered && data.state === 'OPEN' ? (
          <Link className="btn" to={`/cases/new?case=${data.id}`} data-testid="continue-intake">
            Continue the intake
          </Link>
        ) : null}
        {data.historical_entry ? (
          <span
            className="chip neutral"
            data-testid="historical-chip"
            title={
              'This case began before this system did. Its periods are computed from the ' +
              'dates the acts actually happened, not from when they were entered.'
            }
          >
            entered from the past record
          </span>
        ) : null}
      </div>
      <div className="case-acts">
        <Link className="btn" to={`/cases/${data.id}/papers`} data-testid="open-papers">
          Papers on the file
        </Link>
        <button
          type="button"
          className={cgm ? 'btn primary' : 'btn'}
          data-testid="open-chat"
          onClick={onAsk}
        >
          Ask about this case
        </button>
        <Link className="btn" to={`/cases/${data.id}/audit`} data-testid="open-audit">
          Audit trail
        </Link>
      </div>
      <p className="lede" data-testid="case-lede">
        {line ? (
          <span className="name" data-testid="case-identifier-line">
            {line}
          </span>
        ) : (
          <>
            <span className="name">
              {nameEn || readings?.name_en || <span className="deva">{nameHi || '—'}</span>}
            </span>
            {employee['staff_number'] ? (
              <span className="rest">Ticket {employee['staff_number']}</span>
            ) : null}
          </>
        )}
        {stage ? (
          <span className="rest" data-testid="case-stage">
            Stage {stage.n} · {stage.title}
          </span>
        ) : null}
      </p>
      {running.length ? (
        <ul className="deadlines" data-testid="deadlines" aria-label="Deadlines running">
          {running.map((clock) => (
            <Deadline key={clock.key} clock={clock} />
          ))}
        </ul>
      ) : (
        <p className="deadlines none small muted" data-testid="deadlines">
          No deadline is running on this case.
        </p>
      )}
    </header>
  )
}

function Deadline({ clock }: { clock: ClockView }) {
  const period = readPeriod(clock)
  const statutory = clock.authority === 'Statutory'
  return (
    <li className="deadline" data-testid={`deadline-${clock.key}`}>
      <ClockPill from={clock} />
      <span className="dl-label">{clock.label}</span>
      <span className={`dl-authority${statutory ? ' statutory' : ''}`}>
        {statutory ? 'Statutory' : 'Set by the office'}
      </span>
      <CitationChip citation={clock.citation} ruleRefs={clock.rule_refs} />
      <span className="small muted">
        {period.sentence}
        {clock.due_on ? ` Due ${readDate(clock.due_on)}.` : ''}
      </span>
    </li>
  )
}
